/**
 * Claude brand palette — applied app-wide per explicit request: Crail
 * (`#C15F3C`, terracotta), Cloudy (`#B1ADA1`, warm grey), Pampas
 * (`#F4F3EE`, warm off-white), White (`#FFFFFF`). `brandPrimary`/
 * `brandSecondary` are the one seam "Hospital Branding" (STEP 6) hooks
 * into — `createAppTheme` accepts an override for both, defaulting to
 * this palette when no hospital-specific branding is loaded (no real
 * branding data source exists yet to wire this to automatically —
 * disclosed in DESIGN-SYSTEM.md, not fabricated). Semantic status
 * colors (success/warning/error/info) are functional, not brand
 * identity, so they stay as-is.
 */
import type { PaletteOptions } from '@mui/material/styles';

export const brandPrimary = '#C15F3C';
export const brandSecondary = '#8C8577';

export const semanticColors = {
  success: '#2E7D32',
  warning: '#B76E00',
  error: '#C62828',
  info: '#0277BD',
} as const;

/**
 * Enterprise UI Refinement — the sidebar is a FIXED dark tone at all
 * times, independent of the light/dark mode toggle (the AWS/
 * Salesforce/Epic-style persistent dark nav + light content area
 * pattern), so this token group is defined once and used identically
 * by both `buildLightPalette`/`buildDarkPalette` below — it must never
 * vary by mode. Recolored to a warm dark neutral (was navy) to match
 * the Claude brand palette instead of clashing with it. Contrast
 * checked: textPrimary/background ≈13.1:1, textSecondary/background
 * ≈6.6:1 — both comfortably past WCAG AA. Consumed directly by
 * `layouts/AppLayout/Sidebar.tsx`'s own scoped `sx` (not a global
 * MuiDrawer override — see that file's own comment for why), so this
 * shape isn't part of MUI's own `PaletteOptions` type and callers read
 * it as `theme.palette.sidebar.*` via the module augmentation in this
 * same file below.
 */
/**
 * THE SIDEBAR READS THE GOOGLE TOKEN LAYER, NOT A PALETTE OF ITS OWN.
 *
 * These were eight hardcoded browns (`#1E1B18` chrome, `#332C25` selection,
 * `#E2794F` accent) that belonged to no ramp and answered to nothing: the dark
 * bar sat against a light application, and the selected row was a slightly
 * different brown that read as a smudge rather than a state.
 *
 * Every value is now a `--nx-*` custom property from `design-tokens.css` — the
 * same official Google ramps the rest of the application resolves against. Two
 * things follow for free: the selected row is Google's own blue-tint-plus-
 * blue-text treatment (a state you can see across a counter), and dark mode
 * works without a second palette here, because those tokens already swap
 * themselves under `:root[data-theme='dark']`.
 *
 * Kept as MUI theme slots so the ~20 `sidebar.*` references in `Sidebar.tsx`
 * keep working; what changed is where the colours COME FROM.
 */
export const sidebarPalette = {
  background: 'var(--nx-surface)',
  backgroundSelected: 'var(--nx-blue-tint)',
  backgroundHover: 'var(--nx-surface-hover)',
  textPrimary: 'var(--nx-text)',
  textSecondary: 'var(--nx-text-muted)',
  textSelected: 'var(--nx-blue-on-tint)',
  iconColor: 'var(--nx-text-muted)',
  iconColorSelected: 'var(--nx-blue-on-tint)',
  accentBar: 'var(--nx-blue)',
  border: 'var(--nx-border)',
} as const;

/**
 * Enterprise UI Refinement — `UserAvatar`'s deterministic per-user
 * color swatch used to live as its own hardcoded array in that
 * component file; centralized here so every app color lives in one
 * place. 4 of the 8 reuse existing brand/semantic tones directly; the
 * other 4 are additional differentiator hues that have no existing
 * semantic meaning, so they stay as plain hex (an avatar swatch isn't
 * a themeable MUI palette slot the way `sidebar` is).
 */
export const avatarPalette = [
  brandPrimary,
  semanticColors.success,
  semanticColors.error,
  '#6A1B9A',
  '#EF6C00',
  brandSecondary,
  '#5D4037',
  '#455A64',
] as const;

export function buildLightPalette(overridePrimary?: string, overrideSecondary?: string): PaletteOptions {
  return {
    mode: 'light',
    primary: { main: overridePrimary ?? brandPrimary },
    secondary: { main: overrideSecondary ?? brandSecondary },
    success: { main: semanticColors.success },
    warning: { main: semanticColors.warning },
    error: { main: semanticColors.error },
    info: { main: semanticColors.info },
    background: { default: '#F4F3EE', paper: '#FFFFFF' },
    /**
     * BLACK, not the warm browns this used to carry.
     *
     * `#2A241E` (a dark brown) and especially `#6F6A5D` (a light khaki) were
     * chosen to sit with the cream background, but on a real desk monitor at
     * arm's length the secondary tone read as washed out — reported twice from
     * the counter. Legibility outranks warmth: `secondary` is now near-black and
     * carries hierarchy by weight and size, not by fading the text out.
     */
    text: { primary: '#000000', secondary: '#1F1F1F' },
    divider: '#E4E1D8',
    sidebar: sidebarPalette,
  };
}

export function buildDarkPalette(overridePrimary?: string, overrideSecondary?: string): PaletteOptions {
  return {
    mode: 'dark',
    primary: { main: overridePrimary ?? '#E28B62' },
    secondary: { main: overrideSecondary ?? '#C9BFAF' },
    success: { main: '#66BB6A' },
    warning: { main: '#D08A2E' },
    error: { main: '#EF5350' },
    info: { main: '#4FC3F7' },
    background: { default: '#1E1B17', paper: '#2A2420' },
    text: { primary: '#EFEAE3', secondary: '#B3A99A' },
    divider: '#3D362F',
    sidebar: sidebarPalette,
  };
}

declare module '@mui/material/styles' {
  interface Palette {
    sidebar: typeof sidebarPalette;
  }
  interface PaletteOptions {
    sidebar?: typeof sidebarPalette;
  }
}
